import React, { useEffect, useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import { noop } from 'lodash';

import {
    FixedDateMenuOptions,
    TSideOption,
    TabOptions,
    TodayMenuOptions,
    VersionDateMenuOptions,
    defaultValuesByTabOption,
    getDefaultActiveSideMenuOption,
} from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter.utils';
import { Header } from 'components/common/CustomFilters/DateRangeFilter/Hedear/Header';
import { MainContent } from 'components/common/CustomFilters/DateRangeFilter/MainContent/MainContent';
import { SideMenu } from 'components/common/CustomFilters/DateRangeFilter/SideMenu/SideMenu';

import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

import classes from './DateRangeFilter.module.scss';

interface IDateRangeFilterProps {
    onChange: (value: { value?: string[] | string; type?: TabOptions }) => void;
    value: { value?: string[] | string; type?: TabOptions };
    inputDefaultValue: string;
    fixedDateMenuOptions?: { value: string; label: string; dateValue: number[] }[];
    todayMenuOptions?: { value: string; label: string; inputLabel: string }[];
    versionDateMenuOptions?: { value: string; label: string; inputLabel: string }[];
    label?: string;
}

export const DateRangeFilter = ({
    onChange,
    value,
    inputDefaultValue = '',
    fixedDateMenuOptions = FixedDateMenuOptions,
    todayMenuOptions = TodayMenuOptions,
    versionDateMenuOptions = VersionDateMenuOptions,
    label,
}: IDateRangeFilterProps) => {
    const [activeTab, setActiveTab] = useState<TabOptions>(value.type ?? 'TODAY_REF');
    const [activeSideMenuOption, setActiveSideMenuOption] = useState<string>(getDefaultActiveSideMenuOption(value));
    const [inputLabel, setInputLabel] = useState(inputDefaultValue);
    const [rangeDate, setRangeDate] = useState(
        Array.isArray(value.value) && value.value.length === 2 ? value.value.map(Number) : [],
    );

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);

    useEffect(() => {
        setInputLabel(inputDefaultValue);
    }, [inputDefaultValue]);

    useEffect(() => {
        if (value.type !== activeTab) {
            setActiveSideMenuOption(defaultValuesByTabOption[activeTab]);
        } else {
            setActiveSideMenuOption(getDefaultActiveSideMenuOption(value));
        }
    }, [activeTab]);

    useEffect(() => {
        value.type && setActiveTab(value.type);
        setActiveSideMenuOption(getDefaultActiveSideMenuOption(value));
    }, [value]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const sideMenuOptions = useMemo((): TSideOption<string>[] => {
        switch (activeTab) {
            case 'FIXED_DATE':
                return fixedDateMenuOptions;
            case 'TODAY_REF':
                return todayMenuOptions;
            case 'VERSION_DATE_REF':
                return versionDateMenuOptions;
            default:
                return fixedDateMenuOptions;
        }
    }, [activeTab, fixedDateMenuOptions, todayMenuOptions, versionDateMenuOptions]);

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
        setRangeDate([]);
    };

    const handleApply = (value, label) => {
        setInputLabel(label);
        onChange({
            value: value,
            type: activeTab,
        });

        handlePopoverClose();
    };

    const handleClear = () => {
        setInputLabel('');
        setActiveTab('TODAY_REF');
        onChange({
            value: undefined,
            type: undefined,
        });
    };

    return (
        <div data-testid={'battlecards-filters-date-range'}>
            <TextInput
                id={'battlecards-filters-date-range'}
                placeholder={'Select date range...'}
                value={inputLabel}
                onClear={handleClear}
                onClick={handleMenuOpen}
                autoComplete={'off'}
                onChange={noop}
                label={label}
            />
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableScrollLock
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                open={menuOpen}
            >
                <div className={classes.popoverContentWrapper}>
                    <Header activeTab={activeTab} setActiveTab={setActiveTab} />
                    <div className={classes.popoverContent}>
                        <SideMenu
                            menuOptions={sideMenuOptions}
                            activeSideMenuOption={activeSideMenuOption}
                            setActiveSideMenuOption={setActiveSideMenuOption}
                        />
                        <MainContent
                            rangeDate={rangeDate}
                            setRangeDate={setRangeDate}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            onApply={handleApply}
                            activeSideMenuOption={activeSideMenuOption}
                            setActiveSideMenuOption={setActiveSideMenuOption}
                            menuOptions={sideMenuOptions}
                            existingData={value}
                            handlePopoverClose={handlePopoverClose}
                        />
                    </div>
                </div>
            </Popover>
        </div>
    );
};
